import {IndexedDBCache} from "./indexed_db_cache";
import {Tile} from "./tile";
import {fetchAndCacheBlob} from "./fetch_cached_image";
import {elevationFromChannels, TerrainEncoding, TerrainTileSource} from "./terrain_encoding";
import {configuredTerrainSource} from "./elevation_api_config";
import {validateCoordinates, wrapLongitude} from "./validation_utils";
import {MeasurementPrefUtility} from "../helpers/MeasurementPref";

/**
 * ~23 m/px at Irish latitudes with 512 px tiles — comfortably finer than the DEM's own ~30 m grid,
 * at a quarter of z12's tile count. Matches Android. (iOS reads z12 pending its own change.)
 */
export const ELEVATION_ZOOM = 11;

/**
 * Below this map zoom the crosshair readout is suppressed entirely — see `elevationTileRequest`.
 * Distinct from ELEVATION_ZOOM above: this one is about the user's view, not the tile we sample.
 */
export const MINIMUM_MAP_ZOOM = 10;

async function getElevationFromBlob(
  blob: Blob,
  x: number,
  y: number,
  encoding: TerrainEncoding
): Promise<number> {
  // Create an ImageBitmap from the blob
  const imageBitmap = await createImageBitmap(blob);

  // Use OffscreenCanvas if available, otherwise fallback to regular canvas
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(imageBitmap.width, imageBitmap.height)
      : document.createElement('canvas');

  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get 2D context');
  }

  // Draw the ImageBitmap onto the canvas
  ctx.drawImage(imageBitmap, 0, 0);

  // Get pixel data at the specified coordinates
  const pixelData = ctx.getImageData(x, y, 1, 1).data;

  return elevationFromChannels(
    pixelData[0],
    pixelData[1],
    pixelData[2],
    encoding
  );
}

export function getPixelCoordinates(
  coord: { lat: number; lng: number },
  tile: Tile,
  tileSize: number = 512
): { x: number; y: number } {
  const n = Math.pow(2, tile.z);
  const lonDeg = coord.lng;
  const latRad = (coord.lat * Math.PI) / 180;

  const x = ((lonDeg + 180) / 360) * n * tileSize;
  const y =
    ((1 -
        Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) /
      2) *
    n *
    tileSize;

  const pixelX = Math.floor(x - tile.x * tileSize);
  const pixelY = Math.floor(y - tile.y * tileSize);

  return { x: pixelX, y: pixelY };
}

export interface ElevationTileRequest {
  tile: Tile;
  url: string;
  source: TerrainTileSource;
}

/**
 * Which tile to sample for a coordinate, or null when the map is too far out to be worth reading.
 *
 * Two different zooms are at work here. `mapZoom` is how far the user is zoomed in, and only decides
 * *whether* to read at all: below MINIMUM_MAP_ZOOM one crosshair drag crosses whole terrain tiles per
 * throttle tick, so nearly every tick is a cache miss and a fetch, for a reading whose single pixel
 * covers kilometres of screen. The read zoom is separately fixed at ELEVATION_ZOOM and independent of
 * it: elevation is a point query, so one zoom is all that is ever needed, and pinning it is what lets
 * a cached tile be reused however far the user is zoomed in. It is clamped to the source's ceiling
 * because Mapterhorn only guarantees z12 globally.
 */
export function elevationTileRequest(
  coord: { lat: number; lng: number },
  mapZoom: number,
  source: TerrainTileSource = configuredTerrainSource()
): ElevationTileRequest | null {
  if (mapZoom < MINIMUM_MAP_ZOOM) {
    return null;
  }

  const zoom = Math.min(ELEVATION_ZOOM, source.maximumZoom);
  const tile = Tile.fromCoordinates(coord.lat, coord.lng, zoom);

  return { tile, url: source.tileUrl(tile.z, tile.x, tile.y), source };
}

/** Empty string when the map is zoomed too far out — callers render it as "no reading". */
export async function getElevation(
  coord: { lat: number; lng: number },
  mapZoom: number,
  cache: IndexedDBCache
): Promise<string> {
  // Map pointer events hand over unwrapped longitudes once the cursor is over a world copy.
  coord = { lat: coord.lat, lng: wrapLongitude(coord.lng) };
  validateCoordinates(coord.lat, coord.lng);

  const request = elevationTileRequest(coord, mapZoom);
  if (!request) {
    return '';
  }
  const { tile, url: tileUrl, source } = request;

  try {
    // Fetch and cache the tile blob
    const blob = await fetchAndCacheBlob(tileUrl, cache);

    // Calculate the pixel coordinates within the tile
    const pixelCoords = getPixelCoordinates(coord, tile, source.tileSize);

    // Ensure pixel coordinates are within bounds
    if (
      pixelCoords.x < 0 ||
      pixelCoords.y < 0 ||
      pixelCoords.x >= source.tileSize ||
      pixelCoords.y >= source.tileSize
    ) {
      throw new Error('Pixel coordinates are out of bounds');
    }

    // Get elevation from the blob
    const elevation = await getElevationFromBlob(
      blob,
      pixelCoords.x,
      pixelCoords.y,
      source.encoding
    );

    let measurementPreference = MeasurementPrefUtility.preference()
    return '• ' + measurementPreference.smallUnitFormatter(elevation);
  } catch (error) {
    throw new Error(`Failed to get elevation: ${error.message}`);
  }
}



